import type React from 'react';
import { useMemo } from 'react';
import ReactSelect from '@/components/UI/Form/ReactSelect/index.tsx';
import type { MKEERAApiResponseItem } from './dashboard.types';
import { formatDistrict } from './utils/dataProcessing';

interface DistrictFilterProps {
  data: MKEERAApiResponseItem[];
  selectedDistrict: string;
  onDistrictChange: (district: string) => void;
  className?: string;
}

const DistrictFilter: React.FC<DistrictFilterProps> = ({ data, selectedDistrict, onDistrictChange, className }) => {
  // Extract unique districts
  const allDistricts = useMemo(() => {
    // Safety check: ensure data is an array
    if (!Array.isArray(data) || data.length === 0) {
      return [];
    }

    const set = new Set<string>();
    data.forEach((item) => {
      const district = formatDistrict(item?.applicationData?.aldermanicDistrict);
      if (district !== 'Unknown') {
        set.add(district);
      }
    });
    return Array.from(set).sort((a, b) => Number(a) - Number(b));
  }, [data]);

  const districtOptions = useMemo(
    () => [{ value: '', label: 'All Districts' }, ...allDistricts.map((d) => ({ value: d, label: `District ${d}` }))],
    [allDistricts]
  );

  return (
    <div className={className}>
      <div className='min-w-[180px]'>
        <ReactSelect
          options={districtOptions}
          value={districtOptions.find((opt) => opt.value === selectedDistrict)}
          onChange={(option: any) => onDistrictChange((option as { value: string } | null)?.value || '')}
          isSearchable={false}
          menuPlacement='auto'
          menuPosition='fixed'
          aria-label='Select District'
        />
      </div>
    </div>
  );
};

export default DistrictFilter;
